import { call } from 'redux-saga/effects';

import type { FlagValuesMap } from '@/core/flags/flags.state';
import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import { RECOMMENDATION_ENGINES } from '@/productDiscovery/Recommendation/constants';
import type {
  Product,
  WithSponsoredData,
} from '@/productDiscovery/Recommendation/interfaces/product';
import type { Recommendations } from '@/productDiscovery/Recommendation/interfaces/productRecommendation';
import {
  FBT_PLAYLIST_FULLY_SPONSORED_AB_TEST,
  SPONSORED_PRODUCTS_POSITIONS_A,
  SPONSORED_PRODUCTS_POSITIONS_B,
} from '@/productDiscovery/SponsoredProducts/constant';
import { SponsoredProductsLocationType } from '@/productDiscovery/SponsoredProducts/interfaces/SponsoredProducts';
import { getAbtParameterFromSaga } from '@/productDiscovery/SponsoredProducts/utils/getAbtParameter';
import { fetchExistingListByProductGql } from '@/sellerAdsManagement/SponsoredProducts/services/fetchExistingListByProduct';

export function changeElementPosition(
  products: Product[],
  fromIndex: number,
  toIndex: number,
  sponsoredData?: WithSponsoredData,
) {
  const newProducts = [...products];
  if (sponsoredData !== undefined && newProducts.length > fromIndex) {
    const element = products[fromIndex];
    newProducts.splice(fromIndex, 1);
    newProducts.splice(toIndex, 0, {
      ...element,
      ...sponsoredData,
      position: toIndex + 1,
    });
  }

  return newProducts;
}

export function* reorderSponsoredProductsSaga(
  playlists: Recommendations,
  context:
    | SponsoredProductsLocationType.ADD_TO_CART_PAGE
    | SponsoredProductsLocationType.CART_PAGE,
): Generator<any, any, any> {
  const selectFlagValuesMapParam: FlagValuesMap = yield call(
    waitForAndGetFlagValuesSaga,
  );
  const fbtPlaylistFullySponsored = Boolean(
    selectFlagValuesMapParam[FBT_PLAYLIST_FULLY_SPONSORED_AB_TEST],
  );
  const sponsoredProductsPositions = fbtPlaylistFullySponsored
    ? SPONSORED_PRODUCTS_POSITIONS_B
    : SPONSORED_PRODUCTS_POSITIONS_A;

  const fbtPlaylistIndex = playlists.findIndex(
    (pl) => pl.engineName === RECOMMENDATION_ENGINES.FREQUENTLY_BOUGHT_TOGETHER,
  );

  if (fbtPlaylistIndex !== -1) {
    const fbtPlaylist = playlists[fbtPlaylistIndex];

    const productIds: string[] = [];

    fbtPlaylist.products.forEach((p) => {
      productIds.push(p.productId);
    });

    const abt = getAbtParameterFromSaga(selectFlagValuesMapParam);

    if (productIds.length > 0) {
      const result: WithSponsoredData[] = yield call(
        fetchExistingListByProductGql,
        productIds,
        context,
        abt,
      );

      const newPlaylists = [...playlists];

      if (result.length > 0) {
        if (
          fbtPlaylistFullySponsored &&
          result.length > SPONSORED_PRODUCTS_POSITIONS_B.length
        ) {
          const newProductsFBT: Product[] = [];
          fbtPlaylist.products.forEach((p) => {
            const sponsoredData = result.find(
              (r) => r.productId === p.productId,
            );
            if (sponsoredData) {
              newProductsFBT.push({
                ...p,
                ...sponsoredData,
                position: newProductsFBT.length + 1,
              });
            }
          });

          newPlaylists[fbtPlaylistIndex].products = newProductsFBT;
        } else {
          let newProductsFBT = [...fbtPlaylist.products];
          sponsoredProductsPositions.forEach((newPosition, i) => {
            if (result.length > i) {
              const oldPosition = fbtPlaylist.products.findIndex(
                (p) => p.productId === result[i].productId,
              );
              newProductsFBT = changeElementPosition(
                newProductsFBT,
                oldPosition,
                newPosition,
                result[i],
              );
            }
          });

          newPlaylists[fbtPlaylistIndex].products = newProductsFBT;
        }
      }

      newPlaylists[fbtPlaylistIndex].isSpAvailable = result.length > 0;
      newPlaylists[fbtPlaylistIndex].numberSPDisplay = fbtPlaylistFullySponsored
        ? result.length
        : Math.min(result.length, sponsoredProductsPositions.length);

      return newPlaylists;
    }
  }

  return playlists;
}
